import React, { useState, useEffect, useMemo } from 'react';
import { projectsApi, teamsApi } from '../../services/api';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, Inbox, Plus, Loader2 } from 'lucide-react';

const defaultProjectColors = [
  '#6366f1', '#8b5cf6', '#ec4899', '#f43f5e', '#f97316',
  '#eab308', '#22c55e', '#14b8a6', '#06b6d4', '#3b82f6'
];

const ProjectPicker = ({
  selectedProjectId,
  onSelect,
  projects: externalProjects,
  onProjectsChange,
  triggerClassName = '',
  align = 'start',
  side = 'top',
  // Optional custom trigger element (e.g. a toolbar button). Falls back to
  // the default selected-project / Inbox chip.
  trigger = null,
  // When true the default trigger is disabled (e.g. locked task read-only).
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [internalProjects, setInternalProjects] = useState([]);
  const [teams, setTeams] = useState([]);

  // Use external projects if provided, otherwise manage internally
  const projects = externalProjects || internalProjects;
  const setProjects = onProjectsChange || setInternalProjects;

  useEffect(() => {
    // Always load teams for the grouping headers
    teamsApi.getAll()
      .then(res => setTeams(res.data || []))
      .catch(() => {});

    // Only load projects if not provided externally
    if (!externalProjects) {
      projectsApi.getAll()
        .then(res => setInternalProjects(res.data || []))
        .catch(err => console.error('Error loading projects:', err));
    }
  }, [externalProjects]);

  const selectedProject = projects.find(p => p.id === selectedProjectId);

  const filteredProjects = projects.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase())
  );

  // Group projects: personal (no team_id) and by team
  const grouped = useMemo(() => {
    const q = search.toLowerCase();
    const personal = filteredProjects.filter(p => !p.team_id);

    // For each team: if team name matches → show ALL its projects
    //                otherwise → show only projects matching search
    const byTeam = {};
    teams.forEach(team => {
      const teamNameMatches = team.name.toLowerCase().includes(q);
      const teamProjects = projects.filter(p => p.team_id === team.id);
      const projectsToShow = teamNameMatches
        ? teamProjects
        : teamProjects.filter(p => p.name.toLowerCase().includes(q));

      // Only include team if it has something to show
      if (teamProjects.length > 0 || teamNameMatches) {
        byTeam[team.id] = projectsToShow;
      }
    });

    return { personal, byTeam };
  }, [filteredProjects, projects, teams, search]);

  // Show grouped view when searching and there are teams, or when team projects exist without search
  const hasTeamProjects = teams.length > 0 && (search.trim() ? Object.keys(grouped.byTeam).length > 0 : Object.keys(grouped.byTeam).some(tid => grouped.byTeam[tid].length > 0));

  const handleSelect = (projectId) => {
    onSelect(projectId);
    setOpen(false);
    setSearch('');
  };

  const handleQuickCreate = async (name) => {
    if (!name.trim() || creating) return;
    setCreating(true);
    try {
      const color = defaultProjectColors[projects.length % defaultProjectColors.length];
      const response = await projectsApi.create({ name: name.trim(), color });
      const newProject = response.data;
      setProjects([...projects, newProject]);
      onSelect(newProject.id);
      setSearch('');
      setOpen(false);
    } catch (err) {
      console.error('Error creating project:', err);
    } finally {
      setCreating(false);
    }
  };

  const handleCreateInTeam = async (name, teamId) => {
    if (!name.trim() || creating) return;
    setCreating(true);
    try {
      const color = defaultProjectColors[projects.length % defaultProjectColors.length];
      const response = await projectsApi.create({ name: name.trim(), color, team_id: teamId });
      const newProject = response.data;
      setProjects([...projects, newProject]);
      onSelect(newProject.id);
      setSearch('');
      setOpen(false);
    } catch (err) {
      console.error('Error creating project in team:', err);
    } finally {
      setCreating(false);
    }
  };

  const handleKeyDown = (e) => {
    if (
      e.key === 'Enter' &&
      search.trim() &&
      filteredProjects.length === 0 &&
      teams.length === 0
    ) {
      e.preventDefault();
      handleQuickCreate(search);
    }
  };

  const renderProjectButton = (project) => (
    <button
      key={project.id}
      onClick={() => handleSelect(project.id)}
      className={`w-full flex items-center justify-between px-3 py-2 hover:bg-white/5 text-sm ${
        selectedProjectId === project.id ? 'bg-white/5' : ''
      }`}
    >
      <div className="flex items-center gap-2 min-w-0">
        <div
          className="w-3 h-3 rounded shrink-0"
          style={{ backgroundColor: project.color }}
        />
        <span className="text-gray-300 truncate">{project.name}</span>
      </div>
      {selectedProjectId === project.id && (
        <Check className="w-4 h-4 text-amber-400 shrink-0 ml-2" />
      )}
    </button>
  );

  const renderSectionHeader = (label) => (
    <div className="px-3 pt-2 pb-1">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
        {label}
      </span>
    </div>
  );

  const renderCreateInTeam = (team) => (
    <button
      key={`create-in-${team.id}`}
      onClick={() => handleCreateInTeam(search, team.id)}
      disabled={creating}
      className="w-full flex items-center gap-2 px-3 py-2 hover:bg-white/5 text-sm text-amber-400"
    >
      {creating ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <Plus className="w-4 h-4" />
      )}
      <span>{t('ui_common.project_picker.create_in_team', { name: search.trim(), team: team.name })}</span>
    </button>
  );

  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild>
        {trigger || (
        <button
          disabled={disabled}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-white/10 text-sm ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${triggerClassName}`}
        >
          {selectedProject ? (
            <>
              <div
                className="w-3 h-3 rounded"
                style={{ backgroundColor: selectedProject.color }}
              />
              <span className="text-gray-300">{selectedProject.name}</span>
            </>
          ) : (
            <>
              <Inbox className="w-4 h-4 text-blue-400" />
              <span className="text-gray-300">{t('tasks.no_project')}</span>
            </>
          )}
          <ChevronDown className="w-4 h-4 text-gray-500" />
        </button>
        )}
      </PopoverTrigger>
      <PopoverContent
        align={align}
        side={side}
        className="w-64 p-0 bg-[#252526] border-gray-700 z-[100]"
      >
        <div className="p-2 border-b border-gray-700">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('ui_common.project_picker.search_or_create')}
            className="w-full px-3 py-2 bg-white/5 border border-gray-700 rounded-lg text-sm text-white placeholder:text-gray-500 outline-none focus:border-gray-600"
            autoFocus
          />
        </div>
        <div className="max-h-64 overflow-y-auto py-1">
          {/* Quick-create personal project when no personal projects match */}
          {search.trim() && grouped.personal.length === 0 && (
            <button
              onClick={() => handleQuickCreate(search)}
              disabled={creating}
              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-white/5 text-sm text-amber-400"
            >
              {creating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Plus className="w-4 h-4" />
              )}
              <span>{t('ui_common.project_picker.create_personal', { name: search.trim() })}</span>
            </button>
          )}

          {/* Inbox */}
          <button
            onClick={() => handleSelect(null)}
            className={`w-full flex items-center justify-between px-3 py-2 hover:bg-white/5 text-sm ${
              !selectedProjectId ? 'bg-white/5' : ''
            }`}
          >
            <div className="flex items-center gap-2">
              <Inbox className="w-4 h-4 text-blue-400" />
              <span className="text-gray-300">{t('tasks.no_project')}</span>
            </div>
            {!selectedProjectId && <Check className="w-4 h-4 text-amber-400" />}
          </button>

          {hasTeamProjects ? (
            <>
              {/* Personal projects section */}
              {grouped.personal.length > 0 && (
                <>
                  {renderSectionHeader(t('projects.my_projects'))}
                  {grouped.personal.map(renderProjectButton)}
                </>
              )}

              {/* Team sections - only teams that qualify (name matches or have matching projects) */}
              {Object.entries(grouped.byTeam).map(([tid, teamProjects]) => {
                const team = teams.find(tm => tm.id === tid);
                if (!team) return null;
                return (
                  <React.Fragment key={tid}>
                    {renderSectionHeader(t('ui_common.project_picker.team_section', { team: team.name }))}
                    {teamProjects.map(renderProjectButton)}
                    {search.trim() && renderCreateInTeam(team)}
                  </React.Fragment>
                );
              })}
            </>
          ) : (
            /* No team projects - render flat list as before */
            filteredProjects.map(renderProjectButton)
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ProjectPicker;
